// Toast notification system — clean white cards, Apex accent colors.
// Usage: wrap the app in <ToastProvider>, then from any component:
//   const { toast } = useToast();
//   toast.success('Saved'); toast.error('Failed to save', err.message); toast.info('Heads up');
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

type ToastKind = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
  detail?: string;
}

interface ToastApi {
  success: (message: string, detail?: string) => void;
  error: (message: string, detail?: string) => void;
  info: (message: string, detail?: string) => void;
}

interface ToastContextValue {
  toast: ToastApi;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a <ToastProvider>');
  return ctx;
}

// Accent bar color per kind — success/emerald, error/Apex red (primary), info/gray.
const ACCENT: Record<ToastKind, string> = {
  success: 'bg-success',
  error: 'bg-primary-500',
  info: 'bg-gray-400',
};

// Errors linger a bit longer so the user can actually read them.
const TTL: Record<ToastKind, number> = { success: 4000, error: 6000, info: 4000 };

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const idRef = useRef(0);
  const timersRef = useRef<Map<number, number>>(new Map());

  const dismiss = useCallback((id: number) => {
    const timer = timersRef.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, message: string, detail?: string) => {
      const id = ++idRef.current;
      setToasts((prev) => [...prev, { id, kind, message, detail }]);
      const timer = window.setTimeout(() => dismiss(id), TTL[kind]);
      timersRef.current.set(id, timer);
    },
    [dismiss],
  );

  // Stable object so it can safely sit in useCallback/useEffect dependency arrays.
  const toast = useMemo<ToastApi>(
    () => ({
      success: (message, detail) => push('success', message, detail),
      error: (message, detail) => push('error', message, detail),
      info: (message, detail) => push('info', message, detail),
    }),
    [push],
  );

  const value = useMemo<ToastContextValue>(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Toast stack — fixed top-right, above modals/map controls */}
      <div
        aria-live="polite"
        className="fixed top-4 right-4 z-[1100] flex flex-col gap-2 w-[360px] max-w-[calc(100vw-2rem)] pointer-events-none"
      >
        {/* CSS-only enter animation: slight translate + fade */}
        <style>{`
          @keyframes apex-toast-in {
            from { opacity: 0; transform: translateY(-6px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
        {toasts.map((t) => (
          <div
            key={t.id}
            role={t.kind === 'error' ? 'alert' : 'status'}
            className="pointer-events-auto flex items-start gap-3 bg-white rounded-xl border border-gray-200 shadow-lg px-3.5 py-3"
            style={{ animation: 'apex-toast-in 0.18s ease-out' }}
          >
            <span className={`w-1 self-stretch rounded-full shrink-0 ${ACCENT[t.kind]}`} />
            <div className="min-w-0 flex-1 pt-px">
              <p className="text-sm font-semibold text-gray-900 break-words">{t.message}</p>
              {t.detail && (
                <p className="text-xs text-gray-500 mt-0.5 break-words whitespace-pre-line">{t.detail}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss notification"
              className="shrink-0 -mr-1 -mt-0.5 p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
